import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Tag as TagIcon, ArrowRight, Calendar } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

const HERO_IMG = "https://images.pexels.com/photos/6620972/pexels-photo-6620972.jpeg";

export default function BlogIndex() {
  useSEO({
    title: "Blog — Thesis, Formatting & Printing Field Notes · ScholarPrintHub",
    description: "Honest, practical guides for Indian scholars — AKTU thesis format, APA vs Vancouver vs IEEE, Turnitin plagiarism, hard binding options, and more.",
    keywords: "thesis blog, thesis format guide, apa vancouver ieee, turnitin plagiarism, hard binding blog, scholarprinthub blog",
  });
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tag, setTag] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/articles", { params: tag ? { tag } : {} });
        setArticles(data);
      } finally { setLoading(false); }
    })();
  }, [tag]);

  const tags = Array.from(new Set(articles.flatMap((a) => a.tags || []))).slice(0, 12);

  return (
    <div data-testid="blog-index">
      <section className="border-b border-slate-200">
        <div className="container-app py-16 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <span className="section-eyebrow">Blog · Field notes for scholars</span>
            <div className="divider-gold mt-2 mb-4" />
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy">Thesis, formatting & printing — field notes from Lucknow.</h1>
            <p className="mt-6 text-slate-600 text-base max-w-2xl">Everything we learn while helping Indian scholars submit and print their thesis, distilled into short, honest guides.</p>
          </div>
          <div className="lg:col-span-5">
            <img src={HERO_IMG} alt="Printing studio" className="w-full h-80 object-cover border border-slate-200 rounded-sm" />
          </div>
        </div>
      </section>

      {tags.length > 0 && (
        <div className="border-b border-slate-200 bg-slate-50">
          <div className="container-app py-4 flex items-center gap-2 flex-wrap">
            <span className="section-eyebrow mr-2">Filter</span>
            <button data-testid="tag-all" onClick={() => setTag(null)}
                    className={`text-xs border rounded-sm px-3 py-1 ${!tag ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-700 hover:border-navy"}`}>
              All
            </button>
            {tags.map((t) => (
              <button key={t} data-testid={`tag-${t}`} onClick={() => setTag(t)}
                      className={`text-xs border rounded-sm px-3 py-1 flex items-center gap-1 ${tag === t ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-700 hover:border-navy"}`}>
                <TagIcon className="w-3 h-3" /> {t}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="container-app py-16">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>
        ) : articles.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-10 text-center text-slate-500">No articles yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((a) => (
              <Link
                key={a.id}
                data-testid={`blog-card-${a.slug}`}
                to={`/blog/${a.slug}`}
                className="card-flat group flex flex-col overflow-hidden !p-0"
              >
                {a.cover_image_url && (
                  <img src={a.cover_image_url} alt={a.title} className="w-full h-44 object-cover" />
                )}
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-royal">
                    {(a.tags || []).slice(0, 2).map((t) => (
                      <span key={t}>#{t}</span>
                    ))}
                  </div>
                  <h3 className="font-display text-2xl leading-tight mt-3 text-navy">{a.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 flex-1">{a.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {a.published_at ? new Date(a.published_at).toLocaleDateString("en-IN") : ""}</span>
                    <span className="text-royal group-hover:text-gold flex items-center gap-1">Read <ArrowRight className="w-3 h-3" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
